import { CodeText, EmptyState, SectionLabel } from "@/components/common";

import type { SocialPostGenerationTaskDetail } from "../types";

export function LinkedAssetGrid({
  assets,
}: {
  assets: SocialPostGenerationTaskDetail["linkedAssets"];
}) {
  if (assets.length === 0) {
    return (
      <EmptyState
        description="No realized scene images yet."
        className="min-h-40 rounded-2xl bg-muted/20"
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {assets.map((asset) => (
        <div
          key={asset.generatedAssetId}
          className="overflow-hidden rounded-2xl border bg-card shadow-xs"
        >
          <div className="aspect-[4/3] bg-muted/20">
            <img
              src={asset.signedUrl}
              alt={`Scene asset for ${asset.blockId}`}
              className="size-full object-cover"
            />
          </div>
          <div className="space-y-3 p-3 text-xs text-muted-foreground">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-foreground">{asset.blockId}</div>
                <CodeText className="block">
                  {asset.width ?? "?"} x {asset.height ?? "?"}
                </CodeText>
              </div>
              <CodeText className="rounded-full bg-muted px-2 py-0.5">
                {asset.generatedAssetId.slice(0, 8)}
              </CodeText>
            </div>
            {asset.prompt || asset.referenceImages.length > 0 ? (
              <div className="space-y-3 border-t pt-3">
                <SectionLabel className="text-[10px] tracking-wide">Realizer Input</SectionLabel>
                {asset.prompt ? (
                  <div className="space-y-1">
                    <SectionLabel className="text-[10px] tracking-wide">Text Prompt</SectionLabel>
                    <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/30 p-2 whitespace-pre-wrap break-words text-[11px] leading-5 text-foreground">
                      {asset.prompt}
                    </div>
                  </div>
                ) : null}
                <div className="space-y-2">
                  <SectionLabel className="text-[10px] tracking-wide">
                    Attached References ({asset.referenceImages.length})
                  </SectionLabel>
                  {asset.referenceImages.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {asset.referenceImages.map((image) => (
                        <a
                          key={`${asset.generatedAssetId}-${image.objectKey}`}
                          href={image.signedUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block overflow-hidden rounded-md border bg-muted/20 transition-opacity hover:opacity-90"
                          title={image.sourceUrl}
                        >
                          <div className="aspect-square">
                            <img
                              src={image.signedUrl}
                              alt={`Reference image from ${image.sourceUrl}`}
                              className="size-full object-cover"
                            />
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed px-2 py-3 text-[11px]">
                      No reference images were attached to this realized image.
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
